import { useEffect, useState, useCallback } from 'react';
import { platformMdas } from '@/services/platform';
import type { MDA } from '@/services/platform';
import styles from './PlatformStaffServicePage.module.css';

// Staff type from MDA users API
interface Staff {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string | null;
  jobTitle: string | null;
  phone: string | null;
  isActive: boolean;
  securityClearance: number;
  createdAt: string;
  department?: {
    id: string;
    name: string;
    code: string;
  } | null;
  mda?: {
    id: string;
    name: string;
    code: string;
  };
}

// Document request type
interface DocumentRequest {
  id: string;
  requestNumber: string;
  staffId: string;
  staffName: string;
  mdaName: string;
  documentType: string;
  purpose: string;
  status: 'submitted' | 'processing' | 'ready' | 'collected';
  priority: 'normal' | 'urgent';
  requestedAt: Date;
}

// Document types
const DOCUMENT_TYPES = [
  { type: 'service_certificate', name: 'Certificate of Service', icon: '📜', fee: 20 },
  { type: 'employment_verification', name: 'Employment Verification', icon: '✅', fee: 15 },
  { type: 'salary_slip', name: 'Salary Slip', icon: '💰', fee: 5 },
  { type: 'promotion_history', name: 'Promotion History', icon: '📈', fee: 15 },
  { type: 'leave_balance', name: 'Leave Balance Statement', icon: '🏖️', fee: 5 },
  { type: 'good_standing', name: 'Letter of Good Standing', icon: '🎖️', fee: 25 },
];

export function PlatformStaffServicePage() {
  const [mdas, setMdas] = useState<MDA[]>([]);
  const [selectedMda, setSelectedMda] = useState<MDA | null>(null);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<Staff[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [documentRequests, setDocumentRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [staffLoading, setStaffLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'search' | 'profile' | 'documents' | 'requests'>('search');

  // Document generation state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [docPurpose, setDocPurpose] = useState('');
  const [docPriority, setDocPriority] = useState<'normal' | 'urgent'>('normal');

  // Load MDAs on mount
  useEffect(() => {
    async function loadMdas() {
      setLoading(true);
      try {
        const response = await platformMdas.list();
        if (response.success && response.data) {
          setMdas(response.data);
        }
      } catch (err) {
        console.error('Failed to load MDAs:', err);
      } finally {
        setLoading(false);
      }
    }

    loadMdas();
  }, []);

  // Load staff when MDA is selected
  const handleSelectMda = useCallback(async (mda: MDA) => {
    setSelectedMda(mda);
    setSelectedStaff(null);
    setStaffLoading(true);

    try {
      const response = await platformMdas.getUsers(mda.id);
      if (response.success && response.data) {
        const staffWithMda = (response.data as Staff[]).map(s => ({
          ...s,
          mda: { id: mda.id, name: mda.name, code: mda.code }
        }));
        setStaffList(staffWithMda);
        setFilteredStaff(staffWithMda);
      }
    } catch (err) {
      console.error('Failed to load staff:', err);
      setStaffList([]);
      setFilteredStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  // Filter staff by search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredStaff(staffList);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = staffList.filter(staff =>
      staff.firstName.toLowerCase().includes(query) ||
      staff.lastName.toLowerCase().includes(query) ||
      staff.email.toLowerCase().includes(query) ||
      staff.employeeId?.toLowerCase().includes(query)
    );
    setFilteredStaff(filtered);
  }, [searchQuery, staffList]);

  // Select a staff member
  const handleSelectStaff = (staff: Staff) => {
    setSelectedStaff(staff);
    setActiveTab('profile');
  };

  // Generate document
  const handleGenerateDocument = () => {
    if (!selectedStaff || !selectedMda || !selectedDocType || !docPurpose) return;

    const docType = DOCUMENT_TYPES.find(d => d.type === selectedDocType);
    const newRequest: DocumentRequest = {
      id: `req_${Date.now()}`,
      requestNumber: `PLAT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
      staffId: selectedStaff.id,
      staffName: `${selectedStaff.firstName} ${selectedStaff.lastName}`,
      mdaName: selectedMda.name,
      documentType: docType?.name || selectedDocType,
      purpose: docPurpose,
      status: 'submitted',
      priority: docPriority,
      requestedAt: new Date(),
    };

    setDocumentRequests([newRequest, ...documentRequests]);
    setShowGenerateModal(false);
    setSelectedDocType('');
    setDocPurpose('');
    setDocPriority('normal');
    setActiveTab('requests');
  };

  // Format date
  const formatDate = (date: string | Date): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ready':
      case 'collected':
        return 'var(--success)';
      case 'processing':
        return 'var(--warning)';
      case 'submitted':
        return 'var(--info)';
      default:
        return 'var(--text-muted)';
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Staff Service Portal...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>Platform Admin</div>
          <h1 className={styles.title}>Staff Service Portal</h1>
          <p className={styles.subtitle}>
            Search and manage staff records across all MDAs
          </p>
        </div>
        {selectedStaff && selectedMda && (
          <div className={styles.selectedStaffCard}>
            <div className={styles.staffAvatar}>
              {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
            </div>
            <div className={styles.staffInfo}>
              <span className={styles.staffName}>{selectedStaff.firstName} {selectedStaff.lastName}</span>
              <span className={styles.staffMda}>{selectedMda.shortName}</span>
            </div>
            <button className={styles.clearButton} onClick={() => { setSelectedStaff(null); setActiveTab('search'); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'search' ? styles.active : ''}`}
          onClick={() => setActiveTab('search')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <span>Search Staff</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'profile' ? styles.active : ''}`}
          onClick={() => setActiveTab('profile')}
          disabled={!selectedStaff}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>Staff Profile</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'documents' ? styles.active : ''}`}
          onClick={() => setActiveTab('documents')}
          disabled={!selectedStaff}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span>Generate Documents</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'requests' ? styles.active : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <path d="M3 9h18M9 21V9" />
          </svg>
          <span>Requests</span>
          {documentRequests.filter(r => r.status === 'ready').length > 0 && (
            <span className={styles.tabBadge}>{documentRequests.filter(r => r.status === 'ready').length}</span>
          )}
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className={styles.content}>
          {/* MDA Selection */}
          <div className={styles.mdaSection}>
            <h2 className={styles.sectionTitle}>Select Institution (MDA)</h2>
            <p className={styles.sectionDesc}>Choose an MDA to view and manage its staff</p>

            <div className={styles.mdaGrid}>
              {mdas.map((mda) => (
                <div
                  key={mda.id}
                  className={`${styles.mdaCard} ${selectedMda?.id === mda.id ? styles.selected : ''}`}
                  onClick={() => handleSelectMda(mda)}
                >
                  <div className={styles.mdaIcon}>
                    {mda.type === 'ministry' ? '🏛️' : mda.type === 'department' ? '🏢' : '🏭'}
                  </div>
                  <div className={styles.mdaContent}>
                    <span className={styles.mdaName}>{mda.shortName}</span>
                    <span className={styles.mdaFullName}>{mda.name}</span>
                  </div>
                  <span className={`${styles.mdaStatus} ${mda.isActive ? styles.active : styles.inactive}`}>
                    {mda.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Staff Search (only shown after MDA is selected) */}
          {selectedMda && (
            <div className={styles.staffSection}>
              <div className={styles.staffSectionHeader}>
                <h2 className={styles.sectionTitle}>Staff in {selectedMda.shortName}</h2>
                <div className={styles.searchInputWrapper}>
                  <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search by name, email, or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {staffLoading ? (
                <div className={styles.staffLoading}>
                  <div className={styles.loadingSpinner} />
                  <span>Loading staff...</span>
                </div>
              ) : filteredStaff.length > 0 ? (
                <div className={styles.staffGrid}>
                  {filteredStaff.map((staff) => (
                    <div
                      key={staff.id}
                      className={`${styles.staffCard} ${selectedStaff?.id === staff.id ? styles.selected : ''}`}
                      onClick={() => handleSelectStaff(staff)}
                    >
                      <div className={styles.staffCardAvatar}>
                        {staff.firstName[0]}{staff.lastName[0]}
                      </div>
                      <div className={styles.staffCardContent}>
                        <span className={styles.staffCardName}>{staff.firstName} {staff.lastName}</span>
                        <span className={styles.staffCardTitle}>{staff.jobTitle || 'Staff Member'}</span>
                        <span className={styles.staffCardDept}>{staff.department?.name || 'No Department'}</span>
                      </div>
                      <div className={styles.staffCardMeta}>
                        <span className={styles.staffCardId}>{staff.employeeId || 'No ID'}</span>
                        <span className={`${styles.staffCardStatus} ${staff.isActive ? styles.active : styles.inactive}`}>
                          {staff.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyStaff}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <h3>No Staff Found</h3>
                  <p>{searchQuery ? 'Try adjusting your search' : 'This MDA has no registered staff'}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && selectedStaff && selectedMda && (
        <div className={styles.content}>
          <div className={styles.profileSection}>
            <div className={styles.profileHeader}>
              <div className={styles.profileAvatarLarge}>
                {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
              </div>
              <div className={styles.profileHeaderInfo}>
                <h2 className={styles.profileName}>{selectedStaff.firstName} {selectedStaff.lastName}</h2>
                <p className={styles.profileTitle}>{selectedStaff.jobTitle || 'Staff Member'}</p>
                <div className={styles.profileBadges}>
                  <span className={`${styles.badge} ${selectedStaff.isActive ? styles.badgeSuccess : styles.badgeError}`}>
                    {selectedStaff.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={styles.badge}>{selectedMda.shortName}</span>
                  <span className={styles.badge}>Level {selectedStaff.securityClearance}</span>
                </div>
              </div>
              <div className={styles.profileActions}>
                <button className={styles.primaryButton} onClick={() => setActiveTab('documents')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  Generate Document
                </button>
              </div>
            </div>

            <div className={styles.profileGrid}>
              {/* Personal Information */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Personal Information</h3>
                <div className={styles.infoList}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Email</span>
                    <span className={styles.infoValue}>{selectedStaff.email}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Phone</span>
                    <span className={styles.infoValue}>{selectedStaff.phone || 'Not provided'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Employee ID</span>
                    <span className={styles.infoValue}>{selectedStaff.employeeId || 'Not assigned'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Department</span>
                    <span className={styles.infoValue}>{selectedStaff.department?.name || 'Not assigned'}</span>
                  </div>
                </div>
              </div>

              {/* Institution Information */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Institution Information</h3>
                <div className={styles.infoList}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>MDA</span>
                    <span className={styles.infoValue}>{selectedMda.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>MDA Code</span>
                    <span className={styles.infoValue}>{selectedMda.code}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>MDA Type</span>
                    <span className={styles.infoValue}>{selectedMda.type.charAt(0).toUpperCase() + selectedMda.type.slice(1)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Contact Email</span>
                    <span className={styles.infoValue}>{selectedMda.contactEmail || 'Not provided'}</span>
                  </div>
                </div>
              </div>

              {/* Account Information */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Account Information</h3>
                <div className={styles.infoList}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Security Clearance</span>
                    <span className={styles.infoValue}>Level {selectedStaff.securityClearance}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Account Status</span>
                    <span className={styles.infoValue}>{selectedStaff.isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Created</span>
                    <span className={styles.infoValue}>{formatDate(selectedStaff.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className={styles.profileCard}>
                <h3 className={styles.cardTitle}>Quick Actions</h3>
                <div className={styles.quickActions}>
                  <button className={styles.actionButton} onClick={() => setActiveTab('documents')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    Generate Document
                  </button>
                  <button className={styles.actionButton}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    View P-File
                  </button>
                  <button className={styles.actionButton}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    Edit Record
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && selectedStaff && selectedMda && (
        <div className={styles.content}>
          <div className={styles.documentsSection}>
            <div className={styles.documentsHeader}>
              <h2 className={styles.sectionTitle}>Generate Document for {selectedStaff.firstName} {selectedStaff.lastName}</h2>
              <p className={styles.sectionDesc}>Select a document type to generate an official document</p>
            </div>

            <div className={styles.documentTypeGrid}>
              {DOCUMENT_TYPES.map((doc) => (
                <div
                  key={doc.type}
                  className={`${styles.documentTypeCard} ${selectedDocType === doc.type ? styles.selected : ''}`}
                  onClick={() => { setSelectedDocType(doc.type); setShowGenerateModal(true); }}
                >
                  <span className={styles.docIcon}>{doc.icon}</span>
                  <span className={styles.docName}>{doc.name}</span>
                  <span className={styles.docFee}>GH₵ {doc.fee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className={styles.content}>
          <div className={styles.requestsSection}>
            <div className={styles.requestsHeader}>
              <h2 className={styles.sectionTitle}>Document Requests</h2>
              <p className={styles.sectionDesc}>All document requests processed through this portal</p>
            </div>

            {documentRequests.length > 0 ? (
              <div className={styles.requestsList}>
                {documentRequests.map((request) => (
                  <div key={request.id} className={styles.requestCard}>
                    <div className={styles.requestCardHeader}>
                      <div className={styles.requestInfo}>
                        <span className={styles.requestNumber}>{request.requestNumber}</span>
                        <span
                          className={styles.requestStatus}
                          style={{ backgroundColor: `${getStatusColor(request.status)}20`, color: getStatusColor(request.status) }}
                        >
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </div>
                      <span className={styles.requestDate}>{formatDate(request.requestedAt)}</span>
                    </div>

                    <div className={styles.requestDetails}>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Staff</span>
                        <span className={styles.requestValue}>{request.staffName}</span>
                      </div>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>MDA</span>
                        <span className={styles.requestValue}>{request.mdaName}</span>
                      </div>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Document</span>
                        <span className={styles.requestValue}>{request.documentType}</span>
                      </div>
                      <div className={styles.requestDetail}>
                        <span className={styles.requestLabel}>Purpose</span>
                        <span className={styles.requestValue}>{request.purpose}</span>
                      </div>
                    </div>

                    {request.status === 'ready' && (
                      <div className={styles.requestActions}>
                        <button className={styles.primaryButton}>Download</button>
                        <button className={styles.secondaryButton}>Mark Collected</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyRequests}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
                <h3>No Requests Yet</h3>
                <p>Document requests will appear here once created</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generate Document Modal */}
      {showGenerateModal && selectedStaff && selectedMda && (
        <div className={styles.modalOverlay} onClick={() => setShowGenerateModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Generate Document</h2>
              <button className={styles.closeButton} onClick={() => setShowGenerateModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalStaffInfo}>
                <div className={styles.staffAvatar}>
                  {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
                </div>
                <div>
                  <span className={styles.staffName}>{selectedStaff.firstName} {selectedStaff.lastName}</span>
                  <span className={styles.staffMda}>{selectedMda.shortName}</span>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Document Type</label>
                <select
                  className={styles.formSelect}
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                >
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map((doc) => (
                    <option key={doc.type} value={doc.type}>{doc.name} - GH₵ {doc.fee}</option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Purpose *</label>
                <textarea
                  className={styles.formTextarea}
                  value={docPurpose}
                  onChange={(e) => setDocPurpose(e.target.value)}
                  placeholder="Why is this document being requested?"
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Priority</label>
                <div className={styles.priorityOptions}>
                  <label className={`${styles.priorityOption} ${docPriority === 'normal' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      name="priority"
                      value="normal"
                      checked={docPriority === 'normal'}
                      onChange={(e) => setDocPriority(e.target.value as 'normal' | 'urgent')}
                    />
                    <span className={styles.priorityLabel}>Normal</span>
                    <span className={styles.priorityDesc}>3-5 working days</span>
                  </label>
                  <label className={`${styles.priorityOption} ${docPriority === 'urgent' ? styles.selected : ''}`}>
                    <input
                      type="radio"
                      name="priority"
                      value="urgent"
                      checked={docPriority === 'urgent'}
                      onChange={(e) => setDocPriority(e.target.value as 'normal' | 'urgent')}
                    />
                    <span className={styles.priorityLabel}>Urgent</span>
                    <span className={styles.priorityDesc}>1-2 days (+100%)</span>
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.secondaryButton} onClick={() => setShowGenerateModal(false)}>
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleGenerateDocument}
                disabled={!selectedDocType || !docPurpose.trim()}
              >
                Generate Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
